import { accessCodeService } from "../services/accessCodeService";
import { sessionStore } from "../state/session";
import { createOverlay } from "./overlay";
import { createStepController } from "./steps";
import { byId, qsAll } from "./dom";
import { formatDateTime } from "./format";
import type { AccessCode, AccessCodeType } from "../types";

export interface AccessCodeController {
  open(): void;
  close(): void;
}

const STATUS_LABEL: Record<AccessCode["status"], string> = {
  pending: "Active",
  checked_in: "Checked In",
  checked_out: "Checked Out",
  revoked: "Revoked",
};

/** One-hour-from-now, formatted for an <input type="datetime-local">, so the
 *  expiry field starts with a sensible default instead of empty. */
function defaultExpiryLocal(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function initAccessCodeFlow(): AccessCodeController {
  const overlay = createOverlay("sheet-access-code");
  const root = byId("access-code-flow");
  const steps = createStepController(root);

  const newBtn = byId<HTMLButtonElement>("access-code-new-btn");
  const listEl = byId("access-code-list");

  const typeBtns = qsAll<HTMLButtonElement>("[data-code-type]", root);
  const expiryField = byId("access-code-expiry-field");
  const visitorNameInput = byId<HTMLInputElement>("access-code-visitor-name");
  const categoryInput = byId<HTMLSelectElement>("access-code-category");
  const genderInput = byId<HTMLSelectElement>("access-code-gender");
  const maxPeopleInput = byId<HTMLInputElement>("access-code-max-people");
  const expiresAtInput = byId<HTMLInputElement>("access-code-expires-at");
  const descriptionInput = byId<HTMLTextAreaElement>("access-code-description");
  const formError = byId("access-code-form-error");
  const submitBtn = byId<HTMLButtonElement>("access-code-submit-btn");
  const cancelBtn = byId("access-code-cancel-btn");

  const resultEstateLine = byId("access-code-result-estate");
  const resultValue = byId("access-code-result-value");
  const resultHint = byId("access-code-result-hint");
  const closeResultBtn = byId("access-code-close-btn");

  let currentType: AccessCodeType = "one_time";
  let codes: AccessCode[] = [];

  function renderList(): void {
    listEl.innerHTML = "";
    if (codes.length === 0) {
      const empty = document.createElement("li");
      empty.className = "bill-empty";
      empty.textContent = "No access codes generated yet.";
      listEl.appendChild(empty);
      return;
    }
    codes.forEach((c) => {
      const row = document.createElement("li");
      row.className = "bill-row";
      row.innerHTML = `
        <div class="bill-row-main">
          <div class="bill-row-title">${c.visitorName || "Visitor"}</div>
          <div class="bill-row-meta">${c.category || "Guest"} · ${c.codeType === "permanent" ? "Permanent" : formatDateTime(c.expiresAt ?? c.createdAt)}</div>
        </div>
        <div class="bill-row-right">
          <div class="bill-row-amount access-code-display">${c.code}</div>
          <span class="bill-status-badge bill-status-badge--${c.status === "checked_in" ? "paid" : c.status === "revoked" ? "overdue" : "outstanding"}">${STATUS_LABEL[c.status]}</span>
        </div>
      `;
      listEl.appendChild(row);
    });
  }

  async function refresh(): Promise<void> {
    listEl.innerHTML = '<li class="bill-empty bill-empty--loading">Loading…</li>';
    try {
      codes = await accessCodeService.listMyAccessCodes();
      renderList();
    } catch {
      listEl.innerHTML = '<li class="bill-empty">Couldn’t load your access codes.</li>';
    }
  }

  function resetForm(): void {
    currentType = "one_time";
    typeBtns.forEach((b) => b.classList.toggle("is-active", b.dataset.codeType === "one_time"));
    expiryField.hidden = false;
    visitorNameInput.value = "";
    categoryInput.selectedIndex = 0;
    genderInput.selectedIndex = 0;
    maxPeopleInput.value = "1";
    expiresAtInput.value = defaultExpiryLocal();
    descriptionInput.value = "";
    formError.hidden = true;
  }

  typeBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      currentType = btn.dataset.codeType as AccessCodeType;
      typeBtns.forEach((b) => b.classList.toggle("is-active", b === btn));
      expiryField.hidden = currentType === "permanent";
    });
  });

  newBtn.addEventListener("click", () => {
    resetForm();
    steps.show("form");
    visitorNameInput.focus();
  });

  cancelBtn.addEventListener("click", () => steps.show("list"));

  submitBtn.addEventListener("click", async () => {
    const visitorName = visitorNameInput.value.trim();
    const maxPeople = Number(maxPeopleInput.value);

    if (!visitorName) {
      formError.textContent = "Enter the visitor's name.";
      formError.hidden = false;
      return;
    }
    if (!maxPeople || maxPeople < 1) {
      formError.textContent = "Maximum number of people must be at least 1.";
      formError.hidden = false;
      return;
    }
    if (currentType === "one_time" && !expiresAtInput.value) {
      formError.textContent = "Choose when this code should expire.";
      formError.hidden = false;
      return;
    }

    formError.hidden = true;
    submitBtn.disabled = true;
    submitBtn.textContent = "Generating…";

    try {
      const created = await accessCodeService.createAccessCode({
        visitorName,
        category: categoryInput.value,
        gender: genderInput.value,
        maxPeople,
        expiresAt: currentType === "one_time" ? new Date(expiresAtInput.value).toISOString() : undefined,
        description: descriptionInput.value.trim() || undefined,
        codeType: currentType,
      });
      codes = [created, ...codes];
      resultValue.textContent = created.code;
      resultHint.textContent =
        created.codeType === "permanent"
          ? "This code does not expire — share it only with someone who should have standing access."
          : `Valid until ${formatDateTime(created.expiresAt ?? created.createdAt)}.`;
      steps.show("result");
    } catch (err) {
      formError.textContent = err instanceof Error ? err.message : "Couldn't generate a code. Please try again.";
      formError.hidden = false;
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = "Generate Code";
    }
  });

  resultValue.addEventListener("click", () => {
    void navigator.clipboard?.writeText(resultValue.textContent ?? "").then(() => {
      const original = resultHint.textContent;
      resultHint.textContent = "Copied!";
      setTimeout(() => {
        resultHint.textContent = original;
      }, 1200);
    });
  });

  closeResultBtn.addEventListener("click", () => {
    renderList();
    steps.show("list");
  });

  function open(): void {
    resultEstateLine.textContent = sessionStore.user?.name ?? "";
    steps.show("list");
    void refresh();
    overlay.open();
  }

  return { open, close: overlay.close };
}
